import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Plus, UserRound, Sparkles, LogOut, MessageCircleHeart, BookOpen, Settings, Info, ShieldCheck, AlertTriangle, BarChart3, Lock, Bell, Calendar, HelpCircle, Mail, X, FlaskConical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { OnboardingToast } from '@/components/OnboardingNotification';
import TherapistNotifications from '@/components/TherapistNotifications';
import TherapistParentLinkRequestsCard from '@/components/TherapistParentLinkRequestsCard';
import { ICD10_CODES } from '@/data/icd10Codes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function TherapistDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  // Claim-by-code (locum / handover from another therapist).
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    communication_style: 'GLP',
    glp_stage: 'none',
    interests: '',
    regulation_supports: '',
    strengths_notes: '',
    parent_ids: [],
    uses_aac: false,
    aac_system: '',
    diagnostic_codes: [],
    consent_obtained: false,
  });

  const user = JSON.parse(localStorage.getItem('user'));
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showCustomIcd, setShowCustomIcd] = useState(false);
  const [customIcd, setCustomIcd] = useState({ code: '', label: '' });

  useEffect(() => {
    fetchChildren();
    fetchSubscription();
    // Show welcome popup if not dismissed
    const dismissed = localStorage.getItem('playfirst_email_popup_dismissed');
    if (!dismissed) {
      setShowWelcomePopup(true);
    }
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const isTrialExpired = subscription?.effective_plan === 'expired_trial' || 
    (subscription?.trial?.expired && !['free', 'basic', 'pro', 'clinical'].includes(subscription?.plan));

  const dismissWelcomePopup = () => {
    localStorage.setItem('playfirst_email_popup_dismissed', 'true');
    setShowWelcomePopup(false);
  };

  const fetchChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const childrenData = response.data;
      // Sort: demo child first, then alphabetical
      childrenData.sort((a, b) => {
        if (a.is_demo && !b.is_demo) return -1;
        if (!a.is_demo && b.is_demo) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setChildren(childrenData);
      
      // Auto-seed demo child if none exists
      const hasDemo = childrenData.some(c => c.is_demo);
      if (!hasDemo) {
        try {
          await axios.post(`${API}/children/seed-demo`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Re-fetch to include the new demo child
          const refreshed = await axios.get(`${API}/children`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const refreshedData = refreshed.data;
          refreshedData.sort((a, b) => {
            if (a.is_demo && !b.is_demo) return -1;
            if (!a.is_demo && b.is_demo) return 1;
            return (a.name || '').localeCompare(b.name || '');
          });
          setChildren(refreshedData);
        } catch { /* Demo seed failed — non-blocking */ }
      }
    } catch (error) {
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  // Open add child form directly (consent covered in Terms of Service)
  const handleAddChildClick = () => {
    setShowDialog(true);
  };

  const [showHandoverTutorial, setShowHandoverTutorial] = useState(false);
  const [handoverTutorialChildId, setHandoverTutorialChildId] = useState(null);

  const handleClaimChild = async () => {
    setClaimBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/claim-with-therapist-code`,
        { code: claimCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res?.data?.message || 'Child added to your caseload');
      setShowClaimDialog(false);
      setClaimCode('');
      // Capture the new child's id (if the API returns it) so the tutorial
      // can deep-link the "Generate progress report" step.
      setHandoverTutorialChildId(res?.data?.new_child_id || null);
      setShowHandoverTutorial(true);
      fetchChildren();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim child');
    } finally {
      setClaimBusy(false);
    }
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    if (!formData.consent_obtained) {
      toast.error('Please confirm that consent has been obtained for this client\'s information to be processed in PlayFirst.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        glp_stage: formData.glp_stage && formData.glp_stage !== 'none' ? parseInt(formData.glp_stage) : null,
        interests: formData.interests.split(',').map(i => i.trim())
      };
      await axios.post(`${API}/children`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Child profile created!');
      setShowDialog(false);
      fetchChildren();
      setFormData({
        name: '',
        age: '',
        communication_style: 'GLP',
        glp_stage: 'none',
        interests: '',
        regulation_supports: '',
        strengths_notes: '',
        parent_ids: [],
        uses_aac: false,
        aac_system: '',
        diagnostic_codes: [],
        consent_obtained: false,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <UserRound className="w-4 h-4 text-stone-600" />
              <span className="text-sm text-stone-600">{user?.name}</span>
            </div>
            <TherapistNotifications />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Trial Expired Lockout Banner */}
          {isTrialExpired && (
            <Card className="p-6 mb-8 bg-gradient-to-r from-red-50 to-orange-50 border-red-200 rounded-3xl" data-testid="trial-expired-banner">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-1">Your Free Trial Has Ended</h3>
                  <p className="text-sm text-red-700 mb-4">
                    To continue using PlayFirst and access your child profiles, please upgrade to a paid plan. 
                    Your data is safely preserved and will be available once you subscribe.
                  </p>
                  <Link to="/settings">
                    <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="upgrade-now-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Upgrade Now
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          )}

          {/* Sep 2026 — parent-initiated link requests inbox. Shows only
              when there are pending rows; silent otherwise. */}
          <TherapistParentLinkRequestsCard />

          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                Your children
              </h2>
              <p className="text-lg text-stone-600">Connection-focused profiles</p>            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowClaimDialog(true)}
                className="rounded-full"
                disabled={isTrialExpired}
                data-testid="claim-child-btn"
              >
                Claim by code
              </Button>
              <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <Button 
                  data-testid="add-child-btn" 
                  onClick={handleAddChildClick} 
                  className="rounded-full shadow-lg shadow-primary/20"
                  disabled={isTrialExpired}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add child
                </Button>
              <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-child-dialog">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-serif">Create child profile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateChild} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="child-name">Name</Label>
                    <Input
                      id="child-name"
                      data-testid="child-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="First name only"
                      required
                      className="h-12 rounded-2xl"
                    />
                    <p className="text-xs text-stone-400">Do not enter identifying information beyond first name.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="child-age">Age</Label>
                    <Input
                      id="child-age"
                      data-testid="child-age-input"
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      required
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="communication-style">Communication style</Label>
                    <Select
                      value={formData.communication_style}
                      onValueChange={(value) => setFormData({ ...formData, communication_style: value })}
                    >
                      <SelectTrigger data-testid="communication-style-select" className="h-12 rounded-2xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GLP">GLP (Gestalt Language Processing)</SelectItem>
                        <SelectItem value="Analytic">Analytic</SelectItem>
                        <SelectItem value="AAC">AAC (Augmentative Communication)</SelectItem>
                        <SelectItem value="Mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.communication_style === 'GLP' && (
                    <div className="space-y-2">
                      <Label htmlFor="glp-stage">GLP Stage (optional)</Label>
                      <Select
                        value={formData.glp_stage}
                        onValueChange={(value) => setFormData({ ...formData, glp_stage: value })}
                      >
                        <SelectTrigger data-testid="glp-stage-select" className="h-12 rounded-2xl">
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not assessed yet</SelectItem>
                          <SelectItem value="1">Stage 1 - Echolalia</SelectItem>
                          <SelectItem value="2">Stage 2 - Mitigated Gestalts</SelectItem>
                          <SelectItem value="3">Stage 3 - Single Words & Isolated Gestalts</SelectItem>
                          <SelectItem value="4">Stage 4 - First Word Combinations</SelectItem>
                          <SelectItem value="5">Stage 5 - Beginning Grammar</SelectItem>
                          <SelectItem value="6">Stage 6 - Advanced Grammar</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-stone-500">Language examples will adapt to this stage</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="interests">Interests (comma-separated)</Label>
                    <Input
                      id="interests"
                      data-testid="interests-input"
                      placeholder="trains, bubbles, music"
                      value={formData.interests}
                      onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="regulation">Regulation/Sensory supports</Label>
                    <Textarea
                      id="regulation"
                      data-testid="regulation-input"
                      placeholder="Responds well to movement breaks, prefers quiet spaces..."
                      value={formData.regulation_supports}
                      onChange={(e) => setFormData({ ...formData, regulation_supports: e.target.value })}
                      className="rounded-2xl min-h-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="strengths">Strengths notes</Label>
                    <Textarea
                      id="strengths"
                      data-testid="strengths-input"
                      placeholder="Great visual memory, loves problem-solving..."
                      value={formData.strengths_notes}
                      onChange={(e) => setFormData({ ...formData, strengths_notes: e.target.value })}
                      className="rounded-2xl min-h-24"
                    />
                  </div>

                  {/* AAC */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 cursor-pointer" htmlFor="create-uses-aac">
                      <Checkbox
                        id="create-uses-aac"
                        checked={formData.uses_aac}
                        onCheckedChange={(v) => setFormData({ ...formData, uses_aac: !!v })}
                        data-testid="create-uses-aac-checkbox"
                      />
                      Uses AAC (Augmentative & Alternative Communication)
                    </Label>
                    {formData.uses_aac && (
                      <Input
                        placeholder="System / device (e.g. TouchChat, LAMP, PECS)"
                        value={formData.aac_system}
                        onChange={(e) => setFormData({ ...formData, aac_system: e.target.value })}
                        className="h-12 rounded-2xl"
                        data-testid="create-aac-system-input"
                      />
                    )}
                  </div>

                  {/* Diagnostic codes */}
                  <div className="space-y-2">
                    <Label>Diagnostic codes (ICD-10) <span className="text-stone-400 text-xs">— optional</span></Label>
                    {formData.diagnostic_codes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.diagnostic_codes.map((dc, idx) => (
                          <div key={idx} className="px-3 py-1.5 bg-purple-50 text-purple-800 rounded-full flex items-center gap-2 text-sm">
                            <span className="font-mono font-semibold">{dc.code}</span>
                            <span className="text-purple-400">|</span>
                            <span>{dc.label}</span>
                            {dc.custom && (
                              <span className="text-[10px] uppercase tracking-wider bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded-full ml-1">
                                custom
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                diagnostic_codes: formData.diagnostic_codes.filter((_, i) => i !== idx),
                              })}
                              className="text-purple-600 hover:text-purple-900 ml-1"
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Select
                      value=""
                      onValueChange={(value) => {
                        const selected = ICD10_CODES.find((c) => c.code === value);
                        if (selected && !formData.diagnostic_codes.some((c) => c.code === value)) {
                          setFormData({
                            ...formData,
                            diagnostic_codes: [...formData.diagnostic_codes, { code: selected.code, label: selected.label }],
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="h-12 rounded-2xl" data-testid="create-diagnostic-code-select">
                        <SelectValue placeholder="Add a diagnostic code…" />
                      </SelectTrigger>
                      <SelectContent>
                        {ICD10_CODES.map((c) => (
                          <SelectItem
                            key={c.code}
                            value={c.code}
                            disabled={formData.diagnostic_codes.some((x) => x.code === c.code)}
                          >
                            <span className="font-mono">{c.code}</span> — {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Custom code input — for codes not in the standard list (e.g. SA-specific or DSM-style) */}
                    {!showCustomIcd ? (
                      <button
                        type="button"
                        onClick={() => setShowCustomIcd(true)}
                        className="text-xs text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline"
                        data-testid="create-show-custom-icd"
                      >
                        + Can&apos;t find the code? Add your own
                      </button>
                    ) : (
                      <div className="flex flex-wrap gap-2 items-stretch rounded-xl border border-purple-200 bg-purple-50/40 p-2">
                        <Input
                          placeholder="Code (e.g. F84.0)"
                          value={customIcd.code}
                          onChange={(e) => setCustomIcd({ ...customIcd, code: e.target.value })}
                          className="h-10 rounded-xl flex-1 min-w-[110px] font-mono text-sm"
                          data-testid="create-custom-icd-code"
                          maxLength={20}
                        />
                        <Input
                          placeholder="Description"
                          value={customIcd.label}
                          onChange={(e) => setCustomIcd({ ...customIcd, label: e.target.value })}
                          className="h-10 rounded-xl flex-[2] min-w-[140px] text-sm"
                          data-testid="create-custom-icd-label"
                          maxLength={120}
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            const code = customIcd.code.trim();
                            const label = customIcd.label.trim();
                            if (!code || !label) {
                              toast.error('Both the code and description are required');
                              return;
                            }
                            if (formData.diagnostic_codes.some((c) => c.code.toLowerCase() === code.toLowerCase())) {
                              toast.error('That code is already added');
                              return;
                            }
                            setFormData({
                              ...formData,
                              diagnostic_codes: [
                                ...formData.diagnostic_codes,
                                { code, label, custom: true },
                              ],
                            });
                            setCustomIcd({ code: '', label: '' });
                            setShowCustomIcd(false);
                          }}
                          className="h-10 rounded-xl"
                          data-testid="create-custom-icd-add"
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCustomIcd({ code: '', label: '' });
                            setShowCustomIcd(false);
                          }}
                          className="h-10 rounded-xl text-stone-500"
                          data-testid="create-custom-icd-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    <p className="text-xs text-stone-500">You can add or change these anytime from the profile.</p>
                  </div>

                  <label
                    className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${
                      formData.consent_obtained
                        ? 'border-emerald-300 bg-emerald-50/50'
                        : 'border-amber-300 bg-amber-50/40'
                    }`}
                    data-testid="client-consent-label"
                  >
                    <input
                      type="checkbox"
                      checked={!!formData.consent_obtained}
                      onChange={(e) => setFormData({ ...formData, consent_obtained: e.target.checked })}
                      className="mt-1 flex-shrink-0"
                      data-testid="client-consent-checkbox"
                      required
                    />
                    <span className="text-sm text-stone-800 leading-relaxed">
                      I confirm that consent has been obtained for this client's information to be processed in PlayFirst.
                    </span>
                  </label>

                  <Button
                    type="submit"
                    data-testid="create-child-btn"
                    disabled={!formData.consent_obtained}
                    className="w-full h-12 rounded-full"
                  >
                    Create profile
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Claim child by code (locum / handover from another therapist) */}
          <Dialog open={showClaimDialog} onOpenChange={(o) => { if (!claimBusy) setShowClaimDialog(o); }}>
            <DialogContent className="rounded-3xl max-w-md" data-testid="claim-child-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-serif">Claim a shared child</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                Enter the 8-character share code given to you by another PlayFirst therapist.
                You&apos;ll get a copy of the child profile + goals + last 10 progress notes + current Weekly Focus.
              </p>
              <div className="space-y-2 mt-4">
                <Label>Share code</Label>
                <Input
                  value={claimCode}
                  onChange={(e) => setClaimCode(e.target.value.toUpperCase())}
                  placeholder="ABCD2345"
                  maxLength={10}
                  className="font-mono tracking-widest text-center text-lg uppercase"
                  data-testid="claim-code-input"
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowClaimDialog(false)}
                  disabled={claimBusy}
                  className="rounded-full flex-1"
                  data-testid="claim-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleClaimChild}
                  disabled={claimBusy || claimCode.length < 6}
                  className="rounded-full flex-1"
                  data-testid="claim-submit-btn"
                >
                  {claimBusy ? 'Claiming…' : 'Claim'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Handover tutorial — shown once, right after a therapist claims a
              shared child. Walks them through generating a progress report so
              they can catch up on the child's history. */}
          <Dialog open={showHandoverTutorial} onOpenChange={setShowHandoverTutorial}>
            <DialogContent className="rounded-2xl max-w-lg" data-testid="handover-tutorial-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif text-primary-900">
                  Nice work — child added to your caseload
                </DialogTitle>
                <DialogDescription className="text-stone-600">
                  Here's how to get a full handover of this child in three quick steps.
                </DialogDescription>
              </DialogHeader>
              <ol className="space-y-4 mt-4 mb-4">
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <p className="font-medium text-stone-800">Open the child's profile</p>
                    <p className="text-sm text-stone-600">You'll see their profile, current weekly focus, therapy goals, and the last 10 progress notes we copied over from the previous therapist.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <p className="font-medium text-stone-800">Go to Progress → "Generate progress report"</p>
                    <p className="text-sm text-stone-600">Pick a start date <em>before</em> the earliest note we copied. PlayFirst will synthesise everything the previous therapist logged into one handover summary — goals worked on, sessions, strategies that worked, family context.</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <p className="font-medium text-stone-800">Review and adapt</p>
                    <p className="text-sm text-stone-600">Read the report, adjust the goals to your own therapy approach, and use it as your first-session prep. You're ready to see the child with full context.</p>
                  </div>
                </li>
              </ol>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowHandoverTutorial(false)}
                  className="rounded-full"
                  data-testid="handover-tutorial-later-btn"
                >
                  I'll do this later
                </Button>
                <Button
                  onClick={() => {
                    setShowHandoverTutorial(false);
                    if (handoverTutorialChildId) {
                      navigate(`/children/${handoverTutorialChildId}?tab=progress`);
                    }
                  }}
                  className="rounded-full bg-primary-600 hover:bg-primary-700 text-white"
                  data-testid="handover-tutorial-open-btn"
                >
                  Open the child's profile
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading...</p>
            </div>
          ) : children.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <MessageCircleHeart className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No children yet</h3>
              <p className="text-stone-500 mb-6">Create your first child profile to get started</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child, index) => (
                <motion.div
                  key={child.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {isTrialExpired ? (
                    <Card className="p-6 bg-white rounded-3xl border border-stone-200 opacity-75 relative" data-testid={`child-card-${child.id}`}>
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-3xl flex items-center justify-center z-10">
                        <div className="text-center">
                          <Lock className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                          <p className="text-sm text-stone-500">Upgrade to access</p>
                        </div>
                      </div>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-serif text-primary-900 mb-1">{child.name}</h3>
                          <p className="text-sm text-stone-500">{child.age} years old</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <UserRound className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Link to={`/children/${child.id}`}>
                      <Card className={`p-6 bg-white rounded-3xl border hover:shadow-md transition-all cursor-pointer ${child.is_demo ? 'border-purple-200 ring-1 ring-purple-100' : 'border-stone-100'}`} data-testid={`child-card-${child.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-2xl font-serif text-primary-900 mb-1">{child.name}</h3>
                              {child.is_demo && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Demo</span>
                              )}
                            </div>
                            <p className="text-sm text-stone-500">{child.age} years old</p>
                          </div>
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserRound className="w-6 h-6 text-primary" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-stone-600">
                            <span className="font-medium">Communication:</span> {child.communication_style}
                          </p>
                          {child.interests && child.interests.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {child.interests.map((interest, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 bg-secondary/10 text-secondary text-xs rounded-full"
                                >
                                  {interest}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </Card>
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
      
      {/* Onboarding Toast Notifications */}
      <OnboardingToast onNavigate={() => navigate('/activities')} />
      
      {/* Floating Tutorial Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
        className="fixed bottom-6 right-6 z-50"
      >
        <Button
          onClick={() => navigate('/settings?section=tutorial')}
          className="rounded-full w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transition-all"
          data-testid="tutorial-fab"
        >
          <HelpCircle className="w-6 h-6 text-white" />
        </Button>
        <span className="absolute -top-2 -left-2 bg-amber-400 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
          Help
        </span>
      </motion.div>

      {/* Welcome Email Popup */}
      <Dialog open={showWelcomePopup} onOpenChange={(open) => { if (!open) dismissWelcomePopup(); }}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="welcome-email-popup">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">
              Welcome to PlayFirst!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-stone-600 text-center text-sm">
              You're all set up and ready to go.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm font-medium text-amber-800">
                  One quick thing
                </p>
              </div>
              <p className="text-sm text-amber-700">
                We'll be sending you tips, free resources, and exclusive offers to help you get the most out of PlayFirst. These sometimes land in spam or promotions folders.
              </p>
              <div className="space-y-2 text-sm text-stone-700">
                <p className="font-medium">To make sure you don't miss out:</p>
                <ul className="space-y-1.5 ml-1">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">1.</span>
                    Check your spam/promotions folder for our welcome email
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">2.</span>
                    Move it to your primary inbox or mark us as "not spam"
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">3.</span>
                    Add <strong>michaela@playfirstapp.com</strong> to your contacts
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-stone-400 text-center">
              We'd love to hear how you're finding the app — reply to any of our emails and let us know!
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={dismissWelcomePopup} 
              className="w-full h-11 rounded-full"
              data-testid="welcome-popup-dismiss-btn"
            >
              Got it, let's go!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
